import React, { useState, useEffect, useRef } from "react";

//MUI components
import {
    Dialog as MuiDialog,
    DialogTitle as MuiDialogTitle,
    DialogContent as MuiDialogContent,
    Grid,
    TextField,
    MenuItem,
    Typography,
    Tooltip,
    Chip,
    InputAdornment,
    IconButton,
} from "@material-ui/core";
import { withStyles } from "@material-ui/core/styles";

//Icons
import AddIcon from "@material-ui/icons/Add";
import CloseIcon from "@material-ui/icons/Close";

//Custom portal components
import FooterControls from "../FooterComponents/FooterControls";
import IntebotCatalog from "../IntebotCatalog";

//External libraries
import Swal from "sweetalert2";

//styleSheets
import "./Styles/contactsSection.css";

// Catálogo de estados de México para el selector de "Estado".
const mexicanStates = IntebotCatalog.mexicanStates || [];

// Dialog a pantalla completa, con el mismo estilo que el modal de creación de campaña
// (deja visible la barra superior de la app).
const Dialog = withStyles((theme) => ({
    root: {
        marginTop: "56px",
        [theme.breakpoints.up("md")]: {
            marginTop: "64px",
        },
    },
}))(MuiDialog);

const DialogTitle = withStyles(() => ({
    root: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        borderBottom: "1px solid #e3e3e3",
        padding: "8px 20px",
    },
}))(MuiDialogTitle);

const DialogContent = withStyles(() => ({
    root: {
        paddingTop: "20px",
        paddingRight: "20px",
        paddingLeft: "20px",
        // Espacio extra abajo para que la barra inferior fija (FooterControls)
        // no tape los últimos campos.
        paddingBottom: "90px",
    },
}))(MuiDialogContent);

// Campo de detalle de SOLO LECTURA. Se muestra siempre deshabilitado (gris
// atenuado), igual que los campos no editables en el modal de campañas, para
// dejar claro que la información del detalle no se puede modificar.
// TextField deshabilitado con el texto y el borde forzados a gris atenuado,
// para que el detalle se vea claramente no editable (como en el modal de campañas).
const DisabledTextField = withStyles(() => ({
    root: {
        "& .MuiInputBase-input.Mui-disabled": {
            color: "rgba(0, 0, 0, 0.38)",
            WebkitTextFillColor: "rgba(0, 0, 0, 0.38)",
        },
        "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "rgba(0, 0, 0, 0.12)",
        },
    },
}))(TextField);

const ReadOnlyField = ({ label, value }) => (
    <DisabledTextField
        label={label}
        value={value === "" || value === null || value === undefined ? "—" : value}
        fullWidth
        variant="outlined"
        size="small"
        disabled
        InputLabelProps={{ shrink: true }}
    />
);

// Opciones de género: el servicio espera el valor del catálogo "F" / "M".
// "" representa "No especificado".
const genderOptions = [
    { value: "F", label: "Femenino" },
    { value: "M", label: "Masculino" },
    { value: "", label: "No especificado" },
];

// Normaliza el género del contacto al valor del catálogo (F/M) para el selector.
// Acepta variantes previas ("Female"/"Male"/"Femenino"/"Masculino").
const normalizeGenderValue = (raw) => {
    switch (raw) {
        case "F":
        case "Female":
        case "Femenino":
            return "F";
        case "M":
        case "Male":
        case "Masculino":
            return "M";
        default:
            return "";
    }
};

// Normaliza a arreglo (el contacto puede traer null/undefined).
const toArray = (value) => (Array.isArray(value) ? value : value ? [value] : []);

// Formatea una fecha (ms, ISO o { $date }) a texto legible.
const formatDate = (raw) => {
    if (raw === null || raw === undefined || raw === "") return "—";
    let value = raw;
    if (typeof raw === "object") value = raw.$date ?? raw.date ?? null;
    if (value === null || value === undefined) return "—";
    const date = new Date(typeof value === "number" ? value : value);
    if (isNaN(date.getTime())) return "—";
    return date.toLocaleString("es-MX", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
};

// Opciones de sentimiento: valor del backend + etiqueta en español.
const sentimentOptions = [
    { value: "Interested", label: "Interesado" },
    { value: "Confused", label: "Confundido" },
    { value: "Satisfied", label: "Satisfecho" },
    { value: "Unsatisfied", label: "Insatisfecho" },
    { value: "Neutral", label: "Neutral" },
    { value: "Unknown", label: "Desconocido" },
];

// Estado inicial del formulario a partir del contacto.
const buildInitialForm = (contact) => ({
    phone_number: contact?.phone ?? contact?.phone_number ?? "",
    name: contact?.name ?? "",
    gender: normalizeGenderValue(contact?.genderValue ?? contact?.gender),
    age: contact?.age === null || contact?.age === undefined ? "" : contact.age,
    email: contact?.email ?? "",
    state: contact?.state ?? "",
    locality: contact?.locality ?? "",
    interests: toArray(contact?.interests),
    intent: toArray(contact?.intent),
    sentiment: contact?.sentiment ?? "",
    // "status" editable. Por defecto el valor actual del contacto (o "Nuevo" al crear).
    status: contact?.status ?? "",
});

/**
 * Modal a pantalla completa (estilo "crear campaña") para crear/modificar un
 * contacto, mostrando el detalle completo.
 *
 * Campos EDITABLES:
 *  - name (obligatorio), gender, age, email, state, locality,
 *    interests[], intent[], sentiment, status
 *
 * Campos SOLO LECTURA (detalle):
 *  - phone_number (identificador), origin, interactionsCounter, updateDate
 *
 * Props:
 *  - open: controla la visibilidad del modal
 *  - mode: "create" | "edit"
 *  - contact: contacto a editar (cuando mode === "edit")
 *  - onBack(): cierra el modal / regresa al listado
 *  - onSave(payload, mode): persiste; debe devolver true si tuvo éxito
 */
const ContactEditSection = (props) => {
    const { open, mode, contact, onBack, onSave } = props;
    const isEdit = mode === "edit";

    const [form, setForm] = useState(() => buildInitialForm(contact));
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);
    const [interestInput, setInterestInput] = useState("");

    // Snapshot de los valores iniciales del contacto. Sirve para enviar al
    // endpoint SOLO los campos que el usuario realmente modificó.
    const initialFormRef = useRef(buildInitialForm(contact));

    // Reinicia el formulario cada vez que se abre con un contacto distinto
    // (el modal permanece montado y controlado por `open`).
    useEffect(() => {
        if (open) {
            const initial = buildInitialForm(contact);
            setForm(initial);
            initialFormRef.current = initial;
            setErrors({});
            setInterestInput("");
        }
    }, [open, contact]);

    // Opciones de Estado: catálogo de estados de México. Si el contacto ya trae
    // un estado que no está en el catálogo, se incluye para no perderlo.
    const stateOptions = (() => {
        const current = (form.state || "").trim();
        if (current && !mexicanStates.some((s) => s.toLowerCase() === current.toLowerCase())) {
            return [current, ...mexicanStates];
        }
        return mexicanStates;
    })();

    // Opciones de status: incluye el valor actual (aunque sea "Nuevo") + Activo/Inactivo.
    const statusOptions = (() => {
        const base = ["Activo", "Inactivo"];
        const current = (form.status || "").trim();
        if (current && !base.some((s) => s.toLowerCase() === current.toLowerCase())) {
            return [current, ...base];
        }
        return base;
    })();

    const handleChange = (field) => (event) => {
        setForm({ ...form, [field]: event.target.value });
        setErrors({ ...errors, [field]: "" });
    };

    // ---- Manejo de intereses (lista editable) ----
    const addInterest = () => {
        const value = (interestInput || "").trim();
        if (!value) return;
        const exists = form.interests.some((item) => item.toLowerCase() === value.toLowerCase());
        if (!exists) setForm({ ...form, interests: [...form.interests, value] });
        setInterestInput("");
    };

    const handleInterestKeyDown = (event) => {
        if (event.key === "Enter") {
            event.preventDefault();
            addInterest();
        }
    };

    const validate = () => {
        const newErrors = {};
        if (!form.name || !form.name.trim()) {
            newErrors.name = "El nombre es obligatorio";
        }
        const phone = (form.phone_number || "").toString().trim();
        if (!phone) {
            newErrors.phone_number = "El número de teléfono es obligatorio";
        } else if (!/^\d{8,15}$/.test(phone)) {
            newErrors.phone_number = "Ingresa solo dígitos (8 a 15)";
        }
        if (form.age !== "" && form.age !== null) {
            const ageNum = Number(form.age);
            if (Number.isNaN(ageNum) || ageNum < 0 || ageNum > 120) {
                newErrors.age = "Edad inválida";
            }
        }
        const email = (form.email || "").trim();
        if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            newErrors.email = "Correo electrónico inválido";
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Compara dos valores de un campo del formulario. Para arreglos (interests)
    // compara contenido sin importar el orden.
    const isEqualValue = (a, b) => {
        if (Array.isArray(a) || Array.isArray(b)) {
            const arrA = toArray(a).map((x) => x.toString());
            const arrB = toArray(b).map((x) => x.toString());
            if (arrA.length !== arrB.length) return false;
            const sortedA = [...arrA].sort();
            const sortedB = [...arrB].sort();
            return sortedA.every((val, i) => val === sortedB[i]);
        }
        return a === b;
    };

    const handleSave = async () => {
        if (!validate()) return;

        // Incluir el interés que el usuario dejó escrito en el input pero no
        // alcanzó a agregar con Enter/botón "+", para no perderlo al guardar.
        const pendingInterest = (interestInput || "").trim();
        const interests = [...form.interests];
        if (
            pendingInterest &&
            !interests.some((item) => item.toLowerCase() === pendingInterest.toLowerCase())
        ) {
            interests.push(pendingInterest);
        }

        // Valores normalizados actuales de los campos editables.
        const current = {
            name: form.name.trim(),
            gender: form.gender, // valor del catálogo: "F" | "M" | ""
            age: form.age === "" ? null : Number(form.age),
            email: form.email.trim(),
            state: form.state.trim(),
            locality: form.locality.trim(),
            interests: interests,
            // intent y sentiment son solo lectura: no se envían en el guardado.
        };
        // "status" solo es editable al modificar un contacto existente.
        if (isEdit) current.status = form.status;

        let payload;
        if (isEdit) {
            // DIFF: enviar SOLO los campos que cambiaron respecto al original,
            // siempre acompañados del phone_number (la llave del contacto).
            const initial = {
                name: (initialFormRef.current.name || "").trim(),
                gender: initialFormRef.current.gender,
                age:
                    initialFormRef.current.age === "" || initialFormRef.current.age === null
                        ? null
                        : Number(initialFormRef.current.age),
                email: (initialFormRef.current.email || "").trim(),
                state: (initialFormRef.current.state || "").trim(),
                locality: (initialFormRef.current.locality || "").trim(),
                interests: toArray(initialFormRef.current.interests),
                status: initialFormRef.current.status,
            };

            const changed = {};
            Object.keys(current).forEach((field) => {
                if (!isEqualValue(current[field], initial[field])) {
                    changed[field] = current[field];
                }
            });

            // Si no cambió nada, no llamamos al endpoint.
            if (Object.keys(changed).length === 0) {
                Swal.fire({
                    icon: "info",
                    title: "No hay cambios por guardar",
                    timer: 1500,
                    showConfirmButton: false,
                });
                onBack();
                return;
            }

            payload = {
                phone_number: form.phone_number.toString().trim(),
                ...changed,
            };
        } else {
            // Al crear se envía el contacto completo.
            payload = {
                phone_number: form.phone_number.toString().trim(),
                ...current,
            };
        }

        setSaving(true);
        const ok = await onSave(payload, mode);
        setSaving(false);
        if (ok) {
            onBack();
        } else {
            Swal.fire({
                icon: "error",
                title: "No se pudo guardar el contacto",
                text: "Intenta nuevamente más tarde.",
            });
        }
    };

    // Valores de solo lectura (detalle) tomados del contacto.
    const detailOrigin = contact?.origin || "";
    // Nivel de satisfacción: valor calculado. Se muestra la etiqueta en español.
    const detailSentiment = (() => {
        const raw = (contact?.sentiment ?? "").toString().trim();
        if (!raw) return "—";
        const option = sentimentOptions.find((o) => o.value.toLowerCase() === raw.toLowerCase());
        return option ? option.label : raw;
    })();
    // Intenciones: solo lectura (no se pueden modificar).
    const detailIntent = toArray(contact?.intent);
    const detailInteractions =
        contact?.interactionsCounter === null || contact?.interactionsCounter === undefined
            ? "—"
            : contact.interactionsCounter;
    const detailUpdateDate = formatDate(contact?.updateDate);

    return (
        <Dialog open={!!open} fullScreen>
            <DialogTitle disableTypography>
                <h2 style={{ margin: 0, color: "#27315D" }}>
                    {isEdit ? "Modificar contacto" : "Nuevo contacto"}
                </h2>
                <IconButton onClick={onBack} disabled={saving}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                <div style={{ maxWidth: 720, margin: "0 auto" }}>
                    {/* ---- Información editable (card con delimitador) ---- */}
                    <div className="contacts-list-card">
                    <Typography variant="subtitle1" style={{ color: "#27315D", fontWeight: 600, marginBottom: 16 }}>
                        Información del contacto
                    </Typography>

                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Nombre *"
                                value={form.name}
                                onChange={handleChange("name")}
                                fullWidth
                                variant="outlined"
                                size="small"
                                error={!!errors.name}
                                helperText={errors.name}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            {/* En edición el teléfono es el identificador y no se
                                puede modificar: se usa DisabledTextField para que
                                se vea atenuado igual que el bloque de Detalle.
                                En creación es editable (TextField normal). */}
                            {isEdit ? (
                                <DisabledTextField
                                    label="Número de teléfono"
                                    value={form.phone_number}
                                    fullWidth
                                    variant="outlined"
                                    size="small"
                                    disabled
                                    helperText="El número de teléfono no se puede modificar"
                                    InputLabelProps={{ shrink: true }}
                                />
                            ) : (
                                <TextField
                                    label="Número de teléfono"
                                    value={form.phone_number}
                                    onChange={handleChange("phone_number")}
                                    fullWidth
                                    variant="outlined"
                                    size="small"
                                    placeholder="Ej. 521234567890"
                                    error={!!errors.phone_number}
                                    helperText={errors.phone_number}
                                    InputLabelProps={{ shrink: true }}
                                />
                            )}
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                select
                                label="Género"
                                value={form.gender}
                                onChange={handleChange("gender")}
                                fullWidth
                                variant="outlined"
                                size="small"
                                InputLabelProps={{ shrink: true }}
                            >
                                {genderOptions.map((option) => (
                                    <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Edad"
                                type="number"
                                value={form.age}
                                onChange={handleChange("age")}
                                fullWidth
                                variant="outlined"
                                size="small"
                                error={!!errors.age}
                                helperText={errors.age}
                                inputProps={{ min: 0, max: 120 }}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Email"
                                type="email"
                                value={form.email}
                                onChange={handleChange("email")}
                                fullWidth
                                variant="outlined"
                                size="small"
                                placeholder="Ej. correo@dominio.com"
                                error={!!errors.email}
                                helperText={errors.email}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                select
                                label="Entidad Federativa"
                                value={form.state}
                                onChange={handleChange("state")}
                                fullWidth
                                variant="outlined"
                                size="small"
                                InputLabelProps={{ shrink: true }}
                            >
                                <MenuItem value="">
                                    <em>Sin especificar</em>
                                </MenuItem>
                                {stateOptions.map((option) => (
                                    <MenuItem key={option} value={option}>
                                        {option}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Localidad"
                                value={form.locality}
                                onChange={handleChange("locality")}
                                fullWidth
                                variant="outlined"
                                size="small"
                                placeholder="Ej. Zona Metropolitana CDMX"
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        {/* Status editable (permite cambiar a Inactivo). Solo en edición. */}
                        {isEdit && (
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    select
                                    label="Estatus"
                                    value={form.status}
                                    onChange={handleChange("status")}
                                    fullWidth
                                    variant="outlined"
                                    size="small"
                                    InputLabelProps={{ shrink: true }}
                                >
                                    {statusOptions.map((option) => (
                                        <MenuItem key={option} value={option}>
                                            {option}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                        )}

                        {/* Intereses: lista editable (agregar/quitar) */}
                        <Grid item xs={12}>
                            <Typography variant="body2" style={{ color: "#27315D", fontWeight: 600, marginBottom: 6 }}>
                                Intereses
                            </Typography>
                            <TextField
                                value={interestInput}
                                onChange={(e) => setInterestInput(e.target.value)}
                                onKeyDown={handleInterestKeyDown}
                                fullWidth
                                variant="outlined"
                                size="small"
                                placeholder="Ej. campaña de whatsapp"
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <Tooltip title="Agregar interés">
                                                <span>
                                                    <IconButton
                                                        size="small"
                                                        color="primary"
                                                        onClick={addInterest}
                                                        disabled={!interestInput.trim()}
                                                    >
                                                        <AddIcon />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            {form.interests.length > 0 && (
                                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 }}>
                                    {form.interests.map((item, index) => (
                                        <Chip
                                            key={`interest-${index}`}
                                            label={item}
                                            size="small"
                                        />
                                    ))}
                                </div>
                            )}
                        </Grid>

                    </Grid>
                    </div>
                    {/* fin card: Información del contacto */}

                    {/* ---- Detalle (solo lectura). Solo en modo edición. ---- */}
                    {isEdit && (
                        <div className="contacts-list-card" style={{ marginTop: 16 }}>
                            <Typography
                                variant="subtitle1"
                                style={{ color: "#27315D", fontWeight: 600, marginBottom: 16 }}
                            >
                                Detalle
                            </Typography>

                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <ReadOnlyField label="Origen" value={detailOrigin} />
                                </Grid>
                                {/* Nivel de satisfacción: valor calculado, solo lectura. */}
                                <Grid item xs={12} sm={6}>
                                    <ReadOnlyField label="Nivel de Satisfacción" value={detailSentiment} />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <ReadOnlyField label="Total de interacciones" value={detailInteractions} />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <ReadOnlyField label="Última modificación" value={detailUpdateDate} />
                                </Grid>

                                {/* Intenciones: solo lectura (no se pueden modificar).
                                    Se muestran atenuadas para reforzar que no son editables. */}
                                <Grid item xs={12}>
                                    <Typography
                                        variant="body2"
                                        style={{ color: "rgba(0, 0, 0, 0.38)", fontWeight: 600, marginBottom: 6 }}
                                    >
                                        Intenciones
                                    </Typography>
                                    {detailIntent.length > 0 ? (
                                        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                                            {detailIntent.map((item, index) => (
                                                <Chip key={`intent-${index}`} label={item} size="small" disabled />
                                            ))}
                                        </div>
                                    ) : (
                                        <Typography variant="body2" style={{ color: "#8a8a8a" }}>
                                            —
                                        </Typography>
                                    )}
                                </Grid>
                            </Grid>
                        </div>
                    )}
                    {/* fin card: Detalle */}

                </div>
            </DialogContent>

            {/* Barra inferior fija compartida (Cancelar / Guardar), igual que el resto de modales */}
            <FooterControls
                onCancel={onBack}
                onSave={handleSave}
                disableSave={saving || !form.name.trim() || !form.phone_number.toString().trim()}
                saveButtonName={isEdit ? "Guardar cambios" : "Agregar contacto"}
                saveTitle={
                    isEdit
                        ? "¿Estás seguro que deseas guardar los cambios del contacto?"
                        : "¿Estás seguro que deseas agregar el contacto?"
                }
                saveMessage="Esta acción guardará la información del contacto."
            />
        </Dialog>
    );
};

// Se exporta para que la tabla de contactos use las mismas etiquetas en español.
export { sentimentOptions };
export default ContactEditSection;
